import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { sectionsForRole, getDefaultSection, getDefaultTopic } from './helpData.js';
import HelpNav from './HelpNav.jsx';
import HelpContent from './HelpContent.jsx';

// Slide-in help drawer. Sections are filtered to the signed-in user's role, and
// it opens on the section/topic most relevant to the current route.
export default function HelpDrawer({ isOpen, onClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const sections = sectionsForRole(user?.role);

  const [activeSection, setActiveSection] = useState(() => getDefaultSection(location.pathname));
  const [activeTopic, setActiveTopic] = useState(() => getDefaultTopic(location.pathname));

  // On open, jump to the route's section (falling back to the first the role can
  // see) and its matching topic.
  useEffect(() => {
    if (!isOpen) return;
    let sec = getDefaultSection(location.pathname);
    if (!sections.some((s) => s.id === sec)) sec = sections[0]?.id;
    setActiveSection(sec);
    setActiveTopic(getDefaultTopic(location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, location.pathname]);

  // Escape to close + lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const current = sections.find((s) => s.id === activeSection) || sections[0];
  const topicId = current?.topics.some((t) => t.id === activeTopic) ? activeTopic : current?.topics[0]?.id;

  const changeSection = (id) => {
    setActiveSection(id);
    const s = sections.find((x) => x.id === id);
    if (s?.topics.length) setActiveTopic(s.topics[0].id);
  };

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} onClick={onClose} aria-hidden="true" />
      <aside
        className="help-drawer bg-white shadow-lg d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-label="Help and documentation"
      >
        <div className="help-drawer-header d-flex align-items-center justify-content-between text-white px-4 py-3">
          <h2 className="h5 fw-semibold mb-0">
            <i className="fas fa-circle-question me-2" aria-hidden="true" />Help &amp; Documentation
          </h2>
          <button type="button" className="btn-close btn-close-white" aria-label="Close help" onClick={onClose} />
        </div>
        {current && <HelpNav sections={sections} activeSection={current.id} onSectionChange={changeSection} />}
        {current && <HelpContent section={current} activeTopic={topicId} onTopicChange={setActiveTopic} />}
      </aside>
    </>
  );
}
